import { FinanceReport } from '../../types/financeReport';
import { X, Download } from 'lucide-react';
import { exportFinanceReportPdf } from '../../utils/exportPdf';
import { formatNumber } from '../../utils/format';

interface FinanceReportPreviewProps {
  report: FinanceReport;
  onClose: () => void;
}

export function FinanceReportPreview({ report, onClose }: FinanceReportPreviewProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold">Rapport Financier</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => exportFinanceReportPdf(report)}
              className="flex items-center gap-2 px-3 py-1.5 bg-[#00665C] text-white text-sm rounded-lg hover:bg-[#004d45] transition-colors"
              title="Télécharger en PDF"
            >
              <Download size={16} />
              <span className="hidden sm:inline">PDF</span>
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X size={20} />
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Informations générales */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Informations générales</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Date du culte</p>
                <p className="font-medium">{new Date(report.date).toLocaleDateString('fr-FR')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Total des entrées</p>
                <p className="font-bold text-[#00665C] text-lg">{formatNumber(report.totalFinances)} FCFA</p>
              </div>
            </div>
          </section>

          {/* Détail financier */}
          <section>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Détail financier</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                <span className="font-medium text-green-800">Dîmes</span>
                <span className="font-semibold text-green-900">{formatNumber(report.finances.tithes)} FCFA</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg">
                <span className="font-medium text-blue-800">Offrandes ordinaires</span>
                <span className="font-semibold text-blue-900">{formatNumber(report.finances.regularOfferings)} FCFA</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-orange-50 rounded-lg">
                <span className="font-medium text-orange-800">Offrandes spéciales</span>
                <span className="font-semibold text-orange-900">{formatNumber(report.finances.specialOfferings)} FCFA</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-[#00665C]/15 rounded-lg border-2 border-[#00665C]/30">
                <span className="font-bold text-[#004d45]">Total</span>
                <span className="font-bold text-[#004d45] text-xl">{formatNumber(report.totalFinances)} FCFA</span>
              </div>
            </div>
          </section>

          {report.notes && (
            <section>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Notes / Observations</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="whitespace-pre-wrap text-gray-700">{report.notes}</p>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}